import type {
  BotLevel,
  BotMove,
  DrawConfig,
  Lang,
  LexiconSet,
  ModeId,
  RoundState,
} from '@polyglot/engine';
import {
  BOT_PROFILES,
  createRound,
  CURRENT_RULES,
  generateDraw,
  languageRequirement,
  MODES,
  planBotRound,
  playBotRound,
  targetWordsFor,
} from '@polyglot/engine';
import { loadLexicons } from '../lib/lexicons';
import { randomSeed } from '../lib/format';
import { queuePractice } from '../lib/practice';
import { LocalDriver } from './driver';

/**
 * Local (offline-capable) sessions: solo, relax and robot training. Results never enter online
 * leaderboards; finished rounds are saved to the account history as practice (lib/practice). The ongoing round is saved so that a refresh does not reset the clock.
 */
export interface LocalSetup {
  mode: ModeId;
  languages: Lang[];
  bot: { level: BotLevel; languages: Lang[] } | null;
}

export interface LocalSession {
  id: string;
  setup: LocalSetup;
  seed: string;
  rack: string[];
  drawInfo: {
    generatorVersion: string;
    attempts: number;
    source: string;
    lexiconVersions: Partial<Record<Lang, string>>;
  };
  player: RoundState;
  botPlan: BotMove[] | null;
  botFinal: RoundState | null;
  countdownEndsAt: number;
}

const STORE_KEY = 'pa.localSession.v1';
let current: { session: LocalSession; driver: LocalDriver | null; lexicons: LexiconSet } | null =
  null;

function save(session: LocalSession): void {
  try {
    sessionStorage.setItem(STORE_KEY, JSON.stringify(session));
  } catch {
    /* storage unavailable: the round still works, only refresh-resume is lost */
  }
}

function readSaved(): LocalSession | null {
  try {
    const raw = sessionStorage.getItem(STORE_KEY);
    return raw ? (JSON.parse(raw) as LocalSession) : null;
  } catch {
    return null;
  }
}

export class DrawUnavailableError extends Error {
  readonly failureStats: Record<string, number>;
  constructor(failureStats: Record<string, number>) {
    super('no_draw_available');
    this.failureStats = failureStats;
  }
}

const COUNTDOWN_MS = 3000;

export async function startLocalSession(
  setup: LocalSetup,
  fixedSeed?: string,
): Promise<LocalSession> {
  const rules = CURRENT_RULES;
  const mode = MODES[setup.mode];
  const allLangs = [...new Set([...setup.languages, ...(setup.bot?.languages ?? [])])];
  const lexicons = await loadLexicons(allLangs);
  const configs: DrawConfig[] = [setup.languages, ...(setup.bot ? [setup.bot.languages] : [])].map(
    (langs) => ({
      languages: langs,
      requirement: languageRequirement(mode, langs, rules),
      targetWords: targetWordsFor(mode, rules),
    }),
  );
  // A fixed seed gives a deterministic draw (personalised training); otherwise random.
  const seed = fixedSeed ?? randomSeed();
  const draw = generateDraw({ seed, configs, lexicons, rules });
  if (!draw.ok) throw new DrawUnavailableError(draw.failureStats);
  const startAt = Date.now() + COUNTDOWN_MS;
  const player = createRound(
    { mode: setup.mode, rulesVersion: rules.version, rack: draw.rack, languages: setup.languages },
    startAt,
  );
  let botPlan: BotMove[] | null = null;
  let botFinal: RoundState | null = null;
  if (setup.bot) {
    const botRound = createRound(
      {
        mode: setup.mode,
        rulesVersion: rules.version,
        rack: draw.rack,
        languages: setup.bot.languages,
      },
      startAt,
    );
    const profile = BOT_PROFILES[setup.bot.level]!;
    botPlan = planBotRound(botRound, profile, seed, lexicons);
    botFinal = playBotRound(botRound, profile, seed, lexicons).state;
  }
  const session: LocalSession = {
    id: seed,
    setup,
    seed,
    rack: draw.rack,
    drawInfo: {
      generatorVersion: draw.generatorVersion,
      attempts: draw.attempts,
      source: draw.source,
      lexiconVersions: Object.fromEntries(allLangs.map((l) => [l, lexicons[l]!.version])),
    },
    player,
    botPlan,
    botFinal,
    countdownEndsAt: startAt,
  };
  attach(session, lexicons);
  save(session);
  return session;
}

function attach(session: LocalSession, lexicons: LexiconSet): void {
  current?.driver?.dispose();
  const hints = session.setup.mode === 'relax';
  const driver = new LocalDriver(session.player, lexicons, hints, (s) => {
    session.player = s;
    save(session);
    // Finished local rounds are saved to the account history (as unverified practice).
    if (s.status === 'finished') queuePractice(session, s);
  });
  current = { session, driver, lexicons };
}

/** Current session (resumed from storage after a refresh, with the original clock). */
export async function resumeLocalSession(): Promise<{
  session: LocalSession;
  driver: LocalDriver;
} | null> {
  if (current?.driver) return { session: current.session, driver: current.driver };
  const saved = readSaved();
  if (!saved) return null;
  const lexicons = await loadLexicons(
    [...new Set([...saved.setup.languages, ...(saved.setup.bot?.languages ?? [])])],
    saved.drawInfo.lexiconVersions,
  );
  attach(saved, lexicons);
  return { session: current!.session, driver: current!.driver! };
}

export function currentLocalSession(): LocalSession | null {
  return current?.session ?? readSaved();
}

export function clearLocalSession(): void {
  current?.driver?.dispose();
  current = null;
  try {
    sessionStorage.removeItem(STORE_KEY);
  } catch {
    /* ignore */
  }
}

/** Robot progress visible during the round: only the number of words, never the words. */
export function botProgress(
  session: LocalSession,
  now: number,
): { words: number; finished: boolean } {
  if (!session.botPlan || !session.botFinal) return { words: 0, finished: false };
  const elapsed = now - session.player.startedAt;
  const total = session.botFinal.words.length;
  const words = Math.min(total, session.botPlan.filter((m) => m.atMs <= elapsed).length);
  const endAt = session.botFinal.endedAtMs ?? Infinity;
  return { words, finished: elapsed >= endAt };
}
